import type { AdminUnit, Capabilities, CapabilityKey, IntegrationSummary } from './types'

export const TYPE_LABELS: Record<string, string> = {
  pitch: 'Kampeerplaats',
  comfort_pitch: 'Comfortplaats',
  camper_pitch: 'Camperplaats',
  chalet: 'Chalet',
  lodge: 'Lodge',
  safari_tent: 'Safaritent',
  cabin: 'Trekkershut',
  other: 'Overig',
}

export const FEATURE_LABELS: Record<string, string> = {
  electricity: 'Elektra',
  water: 'Water',
  drain: 'Afvoer',
  shade: 'Schaduw',
  pets: 'Huisdieren welkom',
  hardstanding: 'Verharde plaats',
  wifi: 'Wifi',
  private_sanitary: 'Privé sanitair',
  waterfront: 'Aan het water',
  quiet: 'Rustig veld',
}

/** The capability flags as sentences a park owner can read. */
export const CAPABILITY_LABELS: Record<CapabilityKey, string> = {
  availabilityPerUnit: 'beschikbaarheid per plaats',
  availabilityPerType: 'beschikbaarheid per accommodatietype',
  bookingDeeplink: 'boekknop opent het boekingssysteem met plaats en datums',
  directBooking: 'reserveren rechtstreeks via de API',
  webhooks: 'ontvangt wijzigingen via webhooks',
  listsUnits: 'levert een lijst met accommodaties om aan te koppelen',
  prices: 'prijzen beschikbaar',
  onDemandStay: 'wordt per verblijf opgevraagd',
}

/** Half of these are verb phrases, so a "geen " in front of the label above would not be Dutch. */
export const CAPABILITY_LABELS_OFF: Record<CapabilityKey, string> = {
  availabilityPerUnit: 'geen beschikbaarheid per plaats',
  availabilityPerType: 'geen beschikbaarheid per accommodatietype',
  bookingDeeplink: 'geen deeplink naar het boekingssysteem',
  directBooking: 'geen reserveringen via de API',
  webhooks: 'ontvangt geen webhooks',
  listsUnits: 'levert geen lijst met accommodaties',
  prices: 'geen prijzen',
  onDemandStay: 'wordt niet per verblijf opgevraagd',
}

export const CAPABILITY_ORDER: CapabilityKey[] = [
  'availabilityPerUnit',
  'availabilityPerType',
  'prices',
  'bookingDeeplink',
  'directBooking',
  'listsUnits',
  'webhooks',
  'onDemandStay',
]

export function capabilitySentences(capabilities: Capabilities): { key: CapabilityKey; label: string; on: boolean }[] {
  return CAPABILITY_ORDER.map((key) => ({
    key,
    label: capabilities[key] ? CAPABILITY_LABELS[key] : CAPABILITY_LABELS_OFF[key],
    on: capabilities[key],
  }))
}

export function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type
}

export function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] ?? feature
}

export function formatMoney(cents: number, currency = 'EUR', locale = 'nl-NL'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(cents / 100)
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return 'nooit'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

/** "3 minuten geleden" style, used next to the sync chips. */
export function formatAgo(iso: string | null): string {
  if (!iso) return 'nog niet gesynchroniseerd'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const minutes = Math.round((Date.now() - then) / 60_000)
  if (minutes < 1) return 'zojuist'
  if (minutes < 60) return `${minutes} min geleden`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} uur geleden`
  return `${Math.round(hours / 24)} dagen geleden`
}

export type SyncTone = 'fresh' | 'stale' | 'error' | 'off'

const HOUR = 3_600_000

/** Green synced within the hour, amber older, red on a sync error, grey when the koppeling is off. */
export function syncTone(integration: IntegrationSummary): SyncTone {
  if (!integration.enabled) return 'off'
  if (integration.lastSyncError) return 'error'
  if (!integration.lastSyncedAt) return 'stale'
  const then = new Date(integration.lastSyncedAt).getTime()
  if (Number.isNaN(then)) return 'stale'
  return Date.now() - then < HOUR ? 'fresh' : 'stale'
}

export const SYNC_TONE_LABELS: Record<SyncTone, string> = {
  fresh: 'bijgewerkt',
  stale: 'verouderd',
  error: 'fout',
  off: 'uit',
}

/** A unit counts as mapped when at least one enabled integration knows an external unit or type for it. */
export function isMapped(unit: AdminUnit, integrations: IntegrationSummary[]): boolean {
  const relevant = integrations.filter((i) => i.enabled)
  const pool = relevant.length > 0 ? relevant : integrations
  return pool.some((integration) => {
    const mapping = unit.mappings[integration.id]
    return Boolean(mapping && (mapping.externalUnitId || mapping.externalTypeId))
  })
}

export function parseOptionalInt(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number.parseInt(trimmed.replace(',', '.'), 10)
  return Number.isFinite(parsed) ? parsed : null
}

/** Money is typed in euros and stored in cents. */
export function parsePriceCents(value: string): number | null {
  const trimmed = value.trim().replace(',', '.')
  if (trimmed === '') return null
  const parsed = Number.parseFloat(trimmed)
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null
}

export function centsToInput(cents: number | null): string {
  if (cents === null) return ''
  // Dutch UI, so a comma; parsePriceCents accepts either.
  return (cents / 100).toFixed(2).replace('.', ',')
}

/** "4 t/m 6 september", for the stay an on-demand connector reports back after a sync. */
export function formatStay(from?: string, to?: string): string | null {
  if (!from || !to) return null
  // Parse at midday so a timezone offset can never move the date a day.
  const start = new Date(`${from}T12:00:00`)
  const end = new Date(`${to}T12:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return `${from} t/m ${to}`
  const long = (date: Date) => date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  return sameMonth ? `${start.getDate()} t/m ${long(end)}` : `${long(start)} t/m ${long(end)}`
}
